import { LitElement, html, css, unsafeCSS } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { RDPropsetContainer } from "../Container/PropContainer.js";
import { icons } from "../../../Icons.js";

import styles from "./Option.css?raw";

export class propOption extends RDPropsetContainer {
  static styles = [
    super.styles,
    css`
      ${unsafeCSS(styles)}
    `,
  ];
  static properties = {
    value: { type: Object }, // Can be string, number, object, etc.
    bindTo: { type: String, reflect: true },
    ignoreValidation: { type: Boolean, reflect: true },
    selectedIndex: { type: Number, reflect: true },
    selectedText: { type: String, reflect: true },
    disabled: { type: Boolean, reflect: true },
    searchable: { type: Boolean, reflect: true },
    leftIcon: { type: String },
    rightIcon: { type: String },
    showLeftIcon: { type: Boolean, reflect: true },
    showLeftIconOnHover: { type: Boolean, reflect: true },
    showRightIcon: { type: Boolean, reflect: true },
    showRightIconOnHover: { type: Boolean, reflect: true },
    onLeftIconClick: { type: Function },
    onRightIconClick: { type: Function },
    items: { type: Array },
    onSearchInput: { type: Function },
    onSelectionChanged: { type: Function },
    callback: { type: Function },
  };
  constructor() {
    super();
    this.value = "";
    this.selectedIndex = -1;
    this.selectedText = "";
    this.disabled = false;
    this.onSearchInput = null;
    this.onSelectionChanged = null;
    this.callback = null;
    this.searchable = false;
    this.showLeftIcon = true;
    this.showLeftIconOnHover = false;
    this.showRightIcon = true;
    this.showRightIconOnHover = false;
    this.onLeftIconClick = null;
    this.onRightIconClick = null;
    this.leftIcon = "";
    this.rightIcon = "";
    this.bindTo = "value";
    this.ignoreValidation = false;

    /** @type {Array<{leftIcon:string, value:string, rightIcon:string}>} */
    this._rawItems = [
      // { rightIcon: "", value: "-", label: "-", leftIcon: "" },
      // { rightIcon: "", value: "auto", label: "auto", leftIcon: "" },
      // { rightIcon: "", value: "px", label: "px", leftIcon: "" },
      // { rightIcon: "", value: "rem", label: "rem", leftIcon: "" },
      // { rightIcon: "", value: "%", label: "%", leftIcon: "" },
      // { rightIcon: "", value: "em", label: "em", leftIcon: "" },
      // { rightIcon: "", value: "ex", label: "ex", leftIcon: "" },
      // { rightIcon: "", value: "ch", label: "ch", leftIcon: "" },
      // { rightIcon: "", value: "vh", label: "vh", leftIcon: "" },
      // { rightIcon: "", value: "vw", label: "vw", leftIcon: "" },
      // { rightIcon: "", value: "vmin", label: "vmin", leftIcon: "" },
      // { rightIcon: "", value: "vmax", label: "vmax", leftIcon: "" },
      // { rightIcon: "", value: "cm", label: "cm", leftIcon: "" },
      // { rightIcon: "", value: "mm", label: "mm", leftIcon: "" },
      // { rightIcon: "", value: "in", label: "in", leftIcon: "" },
      // { rightIcon: "", value: "pt", label: "pt", leftIcon: "" },
      // { rightIcon: "", value: "pc", label: "pc", leftIcon: "" },
    ];
    this._normalizedItems = null; // Cache for normalized items
  }

  /**
   * Normalizes various array formats into the expected item structure for display
   * while preserving the original value
   * Supports: ['string1', 'string2'], [1, 2, 3], [{value, label, ...}]
   * @param {Array} items - The items array in various formats
   * @returns {Array<{leftIcon:string, value:any, label:string, rightIcon:string, originalValue:any}>}
   */
  _normalizeItems(items) {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    return items.map((item) => {
      // If already in correct format (object with at least 'value' or 'label')
      if (typeof item === "object" && item !== null && !Array.isArray(item)) {
        return {
          ...item, // Preserve any additional properties
          leftIcon: item.leftIcon || "",
          value: item.value !== undefined ? item.value : item.label,
          label: item.label !== undefined ? String(item.label) : String(item.value || ""),
          rightIcon: item.rightIcon || "",
          originalValue: item.value !== undefined ? item.value : item.label,
        };
      }

      // If primitive (string, number, boolean) - preserve original type
      return {
        ...item,
        leftIcon: "",
        value: item, // Keep original value (number, string, etc.)
        label: String(item), // Display as string
        rightIcon: "",
        originalValue: item, // Store original for reference
      };
    });
  }

  get items() {
    return this._rawItems || [];
  }
  get normalizedItems() {
    if (!this._normalizedItems) {
      this._normalizedItems = this._normalizeItems(this._rawItems);
    }
    return this._normalizedItems;
  }

  set items(value) {
    this._rawItems = value;
    this._normalizedItems = null; // Clear cache
    this.requestUpdate();
  }

  get value() {
    return this._value;
  }
  set value(val) {
    const oldVal = this._value;
    if (oldVal === val) return;
    this._value = val;
    this.requestUpdate("value", oldVal);
    if (!this._internalUpdate) {
      this._internalUpdate = true;
      this._updateFromValue(val);
      this._internalUpdate = false;
    }
  }

  get selectedIndex() {
    return this._selectedIndex;
  }
  set selectedIndex(val) {
    const oldVal = this._selectedIndex;
    if (oldVal === val) return;
    this._selectedIndex = val;
    this.requestUpdate("selectedIndex", oldVal);
    if (!this._internalUpdate) {
      this._internalUpdate = true;
      this._updateFromIndex(val);
      this._internalUpdate = false;
    }
  }

  get selectedText() {
    return this._selectedText;
  }
  set selectedText(val) {
    const oldVal = this._selectedText;
    if (oldVal === val) return;
    this._selectedText = val;
    this.requestUpdate("selectedText", oldVal);
    if (!this._internalUpdate) {
      this._internalUpdate = true;
      this._updateFromText(val);
      this._internalUpdate = false;
    }
  }

  _findIndexByValue(searchValue) {
    const items = this.normalizedItems;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const bindValue = this.bindTo || "value";
      // Direct comparison for primitives
      if (item[bindValue] === searchValue) return i;
      // Loose equality for type coercion (e.g., 1 == "1")
      if (item[bindValue] == searchValue) return i;
      // Compare with label
      if (item.label === searchValue || item.label == searchValue) return i;
      // If searchValue is object, compare value properties
      if (typeof searchValue === "object" && searchValue !== null) {
        if (item[bindValue] === searchValue[bindValue] || item[bindValue] == searchValue[bindValue]) {
          return i;
        }
      }
    }
    return -1;
  }

  _updateFromValue(newValue) {
    if (!this.normalizedItems || this.normalizedItems.length === 0) return;
    const index = this._findIndexByValue(newValue);
    if (index !== -1) {
      this._selectedIndex = index;
      this._selectedText = this.normalizedItems[index].label;
      // Keep value as the full normalized item
      if (typeof newValue !== "object" || newValue === null) {
        this._value = this.normalizedItems[index];
      }
    } else {
      this._selectedIndex = -1;
      this._selectedText = "";
      this._value = null;
    }
  }

  _updateFromIndex(newIndex) {
    if (!this.normalizedItems || this.normalizedItems.length === 0) return;
    if (newIndex >= 0 && newIndex < this.normalizedItems.length) {
      this._value = this.normalizedItems[newIndex];
      this._selectedText = this.normalizedItems[newIndex].label;
    } else {
      this._selectedIndex = -1;
      this._selectedText = "";

      this._value = null;
    }
  }
  _updateFromText(newText) {
    if (!this.normalizedItems || this.normalizedItems.length === 0) return;

    const index = this.normalizedItems.findIndex((item) => item.label == newText);
    if (index !== -1) {
      this._selectedIndex = index;
      this._selectedText = this.normalizedItems[index].label;
      this._value = this.normalizedItems[index];
    } else {
      this._selectedIndex = -1;
      this._value = null;
      if (!this.ignoreValidation) {
        this._selectedText = "";
      }
    }
  }
  firstUpdated() {
    super.firstUpdated();
    // Initialize based on priority: selectedIndex > selectedText > value
    // This ensures explicit index/text settings override value
    if (this.selectedIndex >= 0) {
      this._updateFromIndex(this.selectedIndex);
    } else if (this.value !== "" && this.value !== null && this.value !== undefined) {
      this._updateFromValue(this.value);
    } else if (this.selectedText && this.selectedText !== "") {
      this._updateFromText(this.selectedText);
    }
  }

  /**
   * Watch for property changes
   */
  updated(changedProperties) {
    super.updated(changedProperties);

    // Prevent circular updates
    if (this._updating) return;

    if (changedProperties.has("items")) {
      this._internalUpdate = true;
      // Re-sync when items change
      if (this.selectedIndex >= 0) {
        this._updateFromIndex(this.selectedIndex);
      } else if (this.value) {
        this._updateFromValue(this.value);
      }
      this._internalUpdate = false;
    }

    this._renderIcons();
  }

  _optionClicked() {
    if (this.disabled) return;

    const items = this.normalizedItems; // Get cached normalized items
    const target = this.renderRoot.querySelector(".prop-body");

    // Mark selected item
    const itemsWithSelection = items.map((item, index) => ({
      ...item,
      selected: index === this.selectedIndex,
      value: item.value || item.label || "",
      label: item.label || item.value || "",
      leftIcon: item.leftIcon || "",
      rightIcon: item.rightIcon || "",
    }));

    this.panel = {
      size: "auto",
      searchable: this.searchable === true,
      placement: "bottom",
      offsetY: 5,
      offsetX: 0,
      target: target,
      multiSelection: false,
      closeOnSelect: true, // Important for multi-selection!
      items: itemsWithSelection,
      onSelect: (dropdownItem) => {
        this.renderRoot?.querySelector(".option-container")?.focus();

        // Find the selected item
        const index = items.findIndex((item) => item.value == dropdownItem.value);
        if (index !== -1) {
          this._internalUpdate = true; // Prevent update loop
          this.selectedIndex = index;
          this.selectedText = items[index].label;
          this.value = items[index];
          this._internalUpdate = false;
          this.leftIcon = items[index].leftIcon || "";
          this.rightIcon = items[index].rightIcon || "";

          this._onSelectionChanged(index);
          this.dispatchEvent(new CustomEvent("prop-value-changed", { detail: { value: this.value } }));
          this.requestUpdate();
        }
      },
      onOpen: () => {
        this.renderRoot?.querySelector(".arrow-icon")?.classList.add("open");
      },
      onClose: () => {
        this.renderRoot?.querySelector(".arrow-icon")?.classList.remove("open");
      },
      onHover: (item) => {
        // console.log('Hovered pseudo-class:', item.value);
      },
      onSearchInput: (searchValue) => {
        this._onSearchInput(searchValue);
      },
    };
    DropdownPanel.show(this.panel);
    this.requestUpdate();
  }
  _onSelectionChanged(index) {
    let selectedItem = this.items[index];
    if (this.onSelectionChanged && typeof this.onSelectionChanged === "function") {
      this.onSelectionChanged.bind(this)(selectedItem, index);
    }
    if (this.callback && typeof this.callback === "function") {
      this.callback.bind(this)(selectedItem, index);
    }
    this.dispatchEvent(new CustomEvent("prop-value-input", { detail: { value: selectedItem, index: index } }));
  }
  _onSearchInput(e) {
    let searchValue = e.target.value;
    if (this.onSearchInput && typeof this.onSearchInput === "function") {
      this.onSearchInput.bind(this)(searchValue);
    }
    this.dispatchEvent(new CustomEvent("prop-search", { detail: { value: searchValue } }));
  }

  _renderOptionInput() {
    let displayText = "";

    // Always use the value object if it exists (it's the source of truth)
    if (this.value && typeof this.value === "object") {
      displayText = this.value.label || this.value.value;
    } else if (this.selectedIndex >= 0 && this.normalizedItems && this.normalizedItems[this.selectedIndex]) {
      // Use the item from selectedIndex
      displayText = this.normalizedItems[this.selectedIndex].label;
    } else if (this.value !== null && this.value !== undefined && this.value !== "") {
      // Display the raw value
      displayText = String(this.value);
    } else if (this.selectedText) {
      // Fallback to selectedText only if nothing else is set
      displayText = this.selectedText;
    }

    return html`<div class="option-input"><span>${unsafeHTML(displayText)}</span></div>`;
  }
  _renderArrowIcon() {
    return html`<div class="arrow-icon">${unsafeHTML(icons.chevron)}</div>`;
  }
  _renderIcons() {
    const leftIcon = this.value?.leftIcon || "";
    const rightIcon = this.value?.rightIcon || "";
    this.querySelectorAll(".drop-icon").forEach((el) => el.remove());
    if (!leftIcon && !rightIcon) return;
    if (leftIcon && this.showLeftIcon) {
      const left = document.createElement("span");
      left.className = "drop-icon";
      left.slot = "left-icon";
      left.innerHTML = leftIcon;
      this.appendChild(left);
    }

    if (rightIcon && this.showRightIcon) {
      const right = document.createElement("span");
      right.className = "drop-icon";
      right.slot = "right-icon";
      right.innerHTML = rightIcon;
      this.appendChild(right);
    }
  }
  _iconClicked(side, e) {
    if (side === "left" && this.onLeftIconClick && typeof this.onLeftIconClick === "function") {
      e.stopPropagation();
      this.onLeftIconClick.bind(this)();
    } else if (side === "right" && this.onRightIconClick && typeof this.onRightIconClick === "function") {
      e.stopPropagation();
      this.onRightIconClick.bind(this)();
    }
  }
  renderBody() {
    return html`
      <div class="option-container" tabindex="-1" @click="${this._optionClicked}">
        <span @click="${(e) => this._iconClicked("left", e)}" class="left-icon ${this.showLeftIconOnHover ? "hover" : ""}" style="${this.showLeftIcon && this.value?.leftIcon ? "display: flex;" : "display: none;"}"><slot name="left-icon"></slot></span> ${this._renderOptionInput()}
        <span @click="${(e) => this._iconClicked("right", e)}" class="right-icon ${this.showRightIconOnHover ? "hover" : ""}" style="${this.showRightIcon && this.value?.rightIcon ? "display: flex;" : "display: none;"}"><slot name="right-icon"></slot></span>
        ${this._renderArrowIcon()}
      </div>
    `;
  }
}
if (!customElements.get("rd-prop-option")) {
  customElements.define("rd-prop-option", propOption);
}
